/* bfc-campaign.js — Revision 1 item 55: promo / referral / campaign code persistence.
 *
 * The code is the primary attribution mechanism, so it has to survive the whole journey:
 *   printed URL or campaign page  ->  questionnaire  ->  checkout  ->  the subscription.
 *
 * This file owns the FRONT half of that: capture and persistence. It stores the code the moment it
 * is seen and re-attaches it to every internal link into the plan flow, so the code is still present
 * after the customer has wandered around the site. The back half — writing the code onto the Recharge
 * subscription and its recurring orders so it is exportable — belongs to the M3 backend, which reads
 * `BFCCampaign.get()` at checkout hand-off.
 *
 * Precedence: an explicit ?code= (or ?ref= / ?discount=) in the URL always wins over a stored code,
 * because the customer has just arrived from a specific printed piece.
 */
(function () {
  'use strict';

  var KEY = 'bfc_campaign_code';
  var TS = 'bfc_campaign_code_at';
  var SRC = 'bfc_campaign_code_src';          /* url | page | manual — a typed or linked code is never replaced by the WEB10 fallback */
  var ATTR = 'bfc_attribution';               /* Revision 2 item 48: first- and last-touch UTM / landing page / referrer */
  var UTM = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid'];
  var MAX_AGE_DAYS = 90;
  /* Revision 2: utm_campaign is attribution only (bfc_attribution below), never a discount code, so a tagged ad link
     can no longer replace a partner code the customer already holds. */
  var PARAMS = ['code', 'ref', 'discount'];

  function store(get, set) {
    try { return set === undefined ? localStorage.getItem(get) : localStorage.setItem(get, set); }
    catch (e) { return null; }
  }

  function clean(v) {
    if (!v) return '';
    return String(v).trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '').slice(0, 40);
  }

  function fromUrl() {
    var q;
    try { q = new URLSearchParams(window.location.search); } catch (e) { return ''; }
    var section = document.querySelector('[data-bfc-campaign]');
    var names = PARAMS.slice();
    if (section && section.dataset.codeParam) names.unshift(section.dataset.codeParam);
    for (var i = 0; i < names.length; i++) {
      var v = clean(q.get(names[i]));
      if (v) return v;
    }
    return '';
  }

  function fromSection() {
    var section = document.querySelector('[data-bfc-campaign]');
    return section ? clean(section.dataset.code) : '';
  }

  function stored() {
    var v = clean(store(KEY));
    if (!v) return '';
    var at = parseInt(store(TS) || '0', 10);
    if (at && (Date.now() - at) > MAX_AGE_DAYS * 86400000) return '';
    return v;
  }

  function save(code, src) {
    if (!code) return;
    store(KEY, code);
    store(TS, String(Date.now()));
    if (src) store(SRC, src);
  }

  /* 2026-09-29: printed QR links use Shopify's native /discount/KENT10?utm_... route. Shopify only sets a
     discount_code cookie and redirects to /, so the code never reaches the query string. Take the cookie once,
     when its value is new, so a later ?code= link is not overwritten by the same cookie on the next page view. */
  var SEEN = 'bfc_campaign_cookie_seen';
  function fromCookie() {
    var m = document.cookie.match(/(?:^|;\s*)discount_code=([^;]*)/);
    var v = m ? clean(decodeURIComponent(m[1])) : '';
    if (!v || v === store(SEEN)) return '';
    store(SEEN, v);
    return v;
  }

  /* the URL wins, then a fresh /discount/ link, then the campaign page's own code, then whatever we already held */
  var urlCode = fromUrl() || fromCookie();
  var code = urlCode || fromSection() || stored();
  if (urlCode) save(code, 'url');
  else if (fromSection()) save(code, 'page');

  /* ---- attribution: first touch is kept for MAX_AGE_DAYS, last touch moves whenever a tagged link arrives ---- */
  function readAttr() { try { return JSON.parse(store(ATTR) || '{}') || {}; } catch (e) { return {}; } }
  function captureAttribution() {
    var q; try { q = new URLSearchParams(window.location.search); } catch (e) { return; }
    var now = Date.now(), touch = {}, tagged = false;
    UTM.forEach(function (k) { var v = q.get(k); if (v) { touch[k] = String(v).slice(0, 120); tagged = true; } });
    touch.landing = (window.location.pathname + window.location.search).slice(0, 300);
    var ref = document.referrer || '';
    try { if (ref && new URL(ref).host === window.location.host) ref = ''; } catch (e) { ref = ''; }
    if (ref) touch.referrer = ref.slice(0, 300);
    touch.at = new Date(now).toISOString();
    var a = readAttr();
    var firstOld = a.first && a.first.at && (now - Date.parse(a.first.at)) > MAX_AGE_DAYS * 86400000;
    if (!a.first || firstOld) a.first = touch;
    if (tagged || ref || !a.last) a.last = touch;
    store(ATTR, JSON.stringify(a));
  }
  captureAttribution();

  /* show the live code on the campaign page, so a printed ?code= overrides the placeholder */
  var display = document.querySelector('[data-bfc-code-display]');
  if (display && code) display.textContent = code;

  /* carry the code on every internal link into the plan/checkout flow */
  function decorate() {
    if (!code) return;
    var links = document.querySelectorAll('a[href*="/pages/plan"], a[href*="/checkout"], a[data-bfc-code-link]');
    Array.prototype.forEach.call(links, function (a) {
      var href = a.getAttribute('href');
      if (!href || href.charAt(0) === '#') return;
      if (href.indexOf('code=') !== -1) return;
      a.setAttribute('href', href + (href.indexOf('?') === -1 ? '?' : '&') + 'code=' + encodeURIComponent(code));
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', decorate);
  else decorate();

  /* the M3 backend reads this at checkout hand-off */
  window.BFCCampaign = {
    get: function () { return code || stored() || ''; },
    /* src: 'manual' when the customer typed it on the plan page */
    set: function (v, src) { code = clean(v); save(code, src || 'manual'); return code; },
    source: function () { return (code || stored()) ? (store(SRC) || 'stored') : ''; },
    attribution: function () { return readAttr(); },
    /* everything the checkout hand-off sends to the service */
    info: function () { return { code: this.get(), source: this.source(), attribution: readAttr() }; },
    clear: function () { try { localStorage.removeItem(KEY); localStorage.removeItem(TS); localStorage.removeItem(SRC); } catch (e) {} code = ''; }
  };

  /* stamp it onto the questionnaire's choice event so nothing downstream has to re-read storage */
  document.addEventListener('bfc:plan-choice', function (e) {
    if (e && e.detail && !e.detail.campaignCode) e.detail.campaignCode = window.BFCCampaign.get();
  }, true);
})();
